import React, { useState, useEffect, useCallback } from 'react';
import TableManager from '../data/TableManager';
import { FaTimes } from 'react-icons/fa';
import { stripHtmlTags } from '../../utils/htmlUtils';

// ONFIGURAZIONE COLONNE: Basata sulla DDL SKE_TestModifiche
const temColumnConfig = [
  // Fields not shown in forms or table (visibility 0) 
  "TEM_Id_TestModifiche=0,ID",
  "TEM_IdRif_TestModifiche=0,ID Rif.",
  
  // IMPORTANTE: Campo collegamento alla macro funzione (ora visibile in tabella per il lookup)
  "TEM_Id_MacroFunzioni=1,ID Macro Funzione,lookupName=macrofunzionilookup", 
  "TEM_Id_Richieste=1,ID Richiesta,lookupName=richiestelookup",
  
  // General form fields (visibility 2 = form + tabella)
  "TEM_DescrizioneModifica=2,Descrizione Modifica,required", 
  "TEM_CriterioTest=2,Criterio Test,required", 
  
  // Test result field (visibility 2 = form + tabella)
  "TEM_TestSuperato=2,Test Superato,isBoolean=true",
  
  // **IMPORTANTE**: User fields - usa lookup base semplice
  "TEM_IdRichiedente_Utenti=1,Utente Richiedente,required,lookupName=utentiLookup",
  "TEM_IdEsecutore_Utenti=1,Utente Esecutore,required,lookupName=utentiLookup",
  
  // System fields (visibility 0 = nascosti ma gestiti dal backend)
  "TEM_InizioValidita=0,Inizio Validità,type=datetime", 
  "TEM_FineValidita=0,Fine Validità,type=datetime", 
  "TEM_StatiRecord=0,Stato Record", 

  // Audit fields (visibility 0 = nascosti, gestiti dal backend)
  "TEM_UtenteInserimento=0,Utente Inserimento",
  "TEM_DataOraInserimento=0,Data/Ora Inserimento",
  "TEM_TerminaleInserimento=0,Terminale Inserimento",
  "TEM_UtenteModifica=0,Utente Modifica",
  "TEM_DataOraModifica=0,Data/Ora Modifica",
  "TEM_TerminaleModifica=0,Terminale Modifica",
  "TEM_Timestamp=0,Timestamp DB",
];

// CONFIGURAZIONE LAYOUT: Solo i campi che saranno visibili (visibility=1 o 2)
const temFormLayoutConfig = {
  sections: [
    {
      title: "Dettagli Test",
      fields: ["TEM_DescrizioneModifica", "TEM_CriterioTest"]
    },
    {
      title: "Collegamento Richiesta",
      fields: ["TEM_Id_Richieste"]
    },
    {
      title: "Risultati e Responsabilità",
      layout: "twoColumns",
      columns: [
        {
          title: "Risultato Test",
          icon: "FaCheckCircle",
          fields: [["TEM_TestSuperato"]]
        },
        {
          title: "Utenti Coinvolti", 
          icon: "FaUsers",
          fields: [
            ["TEM_IdRichiedente_Utenti"], 
            ["TEM_IdEsecutore_Utenti"]
          ]
        }
      ]
    }
  ]
};

// **SEMPLIFICATO**: Lookup config base senza complicazioni
const temLookupConfig = {
  utentiLookup: {
    tableName: 'SKE_Utenti',
    valueField: 'UTE_Id_Utenti', 
    labelField: 'UTE_NomeUtente', 
    additionalFields: ['UTE_Email'],
  },
  richiestelookup: {
    tableName: 'SKE_Richieste',
    valueField: 'RIC_Id_Richieste', 
    labelField: 'RIC_DescrizioneRichiesta',
  },
  macrofunzionilookup: {
    tableName: 'SKE_MacroFunzioni',
    valueField: 'MAC_Id_MacroFunzioni',
    labelField: 'MAC_Nome',
    additionalFields: ['MAC_Descrizione'],
  }
};

const temInputTypes = {
  // Campi visibili
  TEM_DescrizioneModifica: 'textarea',
  TEM_CriterioTest: 'textarea',
  
  // Campo booleano
  TEM_TestSuperato: 'boolean',
  
  // Lookup fields
  TEM_IdRichiedente_Utenti: 'select',
  TEM_IdEsecutore_Utenti: 'select',
  TEM_Id_Richieste: 'select',
  TEM_Id_MacroFunzioni: 'select',
  

};

const TestModificheModal = ({ 
  isOpen, 
  onClose, 
  macroFunzioneId, 
  themeColor = 'emerald',
  onDataChanged,
  editTestId = null,
  macroFunzioneData = null,
  mode = 'list',
}) => {
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset quando si apre la modal
  useEffect(() => {
    if (isOpen) {
      setRefreshKey(prev => prev + 1);
    }
  }, [isOpen]);

  // **SOLUZIONE DEFINITIVA**: Funzione di validazione che intercetta i dati
  const validateAndProceed = useCallback((data, successCallback) => {

    
    const richiedenteId = data.TEM_IdRichiedente_Utenti;
    const esecutoreId = data.TEM_IdEsecutore_Utenti;

    // Controlla se entrambi i campi sono presenti e se i loro valori sono uguali
    if (richiedenteId && esecutoreId && String(richiedenteId) === String(esecutoreId)) {
      console.error('[TestModificheModal] VALIDAZIONE FALLITA: Utenti duplicati rilevati.');
      alert("❌ ERRORE: L'utente richiedente e l'utente esecutore devono essere diversi!\n\nIl salvataggio è stato bloccato.");
      return; // Blocca l'operazione
    }

    // Se la validazione passa, esegui il callback di successo originale

    if (successCallback) {
      successCallback(data);
    }
  }, []);

  // Handler per la chiusura
  const handleClose = useCallback(() => {
    console.log('🚪 [TestModificheModal] Chiusura modale');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const initialFiltersForTEM = { TEM_Id_MacroFunzioni: macroFunzioneId };
  
  const defaultValuesForCreateTEM = { 
    TEM_Id_MacroFunzioni: macroFunzioneId,
    TEM_Id_Richieste: 0,
    TEM_DescrizioneModifica: stripHtmlTags(macroFunzioneData?.MAC_Modifiche) || '',
    TEM_TestSuperato: false,
    TEM_StatoRecord: 'A'
  };

  console.log("🔍 [TestModificheModal] macroFunzioneId:", macroFunzioneId);
  console.log("🔍 [TestModificheModal] editTestId:", editTestId);

  const temFilterConfig = [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4">
      <div className={`bg-white dark:bg-gray-800 p-1 sm:p-2 rounded-xl shadow-2xl w-full max-w-6xl border border-${themeColor}-500 dark:border-${themeColor}-700 flex flex-col max-h-[90vh]`}>
        <div className="flex justify-between items-center p-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-xl font-semibold text-${themeColor}-700 dark:text-${themeColor}-200`}>
            {mode === 'edit' ? 'Modifica Test' : 'Gestione Test Modifiche'} - {macroFunzioneData?.MAC_Nome ? `${macroFunzioneData.MAC_Nome} (ID: ${macroFunzioneId})` : `Macro ID: ${macroFunzioneId}`}
          </h3>
          <button onClick={handleClose} aria-label="Close modal" className={`p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700`}>
            <FaTimes size={20} />
          </button>
        </div>
        
        <div className="mx-3 mt-3 p-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="text-blue-600 dark:text-blue-400 font-medium">
              ℹ️ Nota Importante
            </div>
          </div>
          <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            L'utente richiedente e l'utente esecutore devono essere persone diverse. Il sistema impedirà il salvataggio se selezioni lo stesso utente per entrambi i ruoli.
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto p-0 sm:p-2">
          <TableManager
            key={`test-modifiche-${macroFunzioneId}-${refreshKey}-${editTestId || 'new'}-${mode}`}
            tableName="SKE_TestModifiche"
            primaryKey="TEM_Id_TestModifiche"
            pageTitle=""
            columnConfig={temColumnConfig}
            filterConfig={temFilterConfig}
            lookupConfig={temLookupConfig}
            inputTypes={temInputTypes}
            initialFilters={initialFiltersForTEM}
            autoSearchOnInitialFilters={true}
            defaultValuesForCreate={defaultValuesForCreateTEM}
            singularRecordName="Test Modifica"
            enableCreate={mode === 'list'}
            enableEdit={true}
            enableDelete={mode === 'list'}
            formLayoutConfig={temFormLayoutConfig}
            autoSelectAndEdit={editTestId}
            hideTable={mode === 'edit'}
            // ✅ MODIFICATO: Usa solo il campo primario per l'ordinamento, poi il backend gestirà il secondario
            defaultSortField="TEM_TestSuperato"
            defaultSortDirection="asc"
            // ✅ NUOVO: Aggiungi un override per l'ordinamento iniziale che includa entrambi i campi
            initialSortBy={[
              { id: 'TEM_TestSuperato', desc: false },
              { id: 'TEM_DataOraInserimento', desc: true }
            ]}
            onCreateSuccess={(data) => {
          
              return validateAndProceed(data, onDataChanged);
            }}
            onUpdateSuccess={(data) => {
              console.log('🎯 [TestModificheModal] onUpdateSuccess CHIAMATO con data:', data);
              return validateAndProceed(data, onDataChanged);
            }}
            onDeleteSuccess={onDataChanged}
          />
        </div>
      </div>
    </div>
  );
};

export default TestModificheModal;